import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import API from "../api";

const AuthContext = createContext(null);

// v19.9 — Safety net for the platform-owner (Donnell). If the backend
// response's `is_admin` boolean is missing OR the stale-bundle problem
// caused it to be false, ALSO treat any email starting with "donnell"
// (case-insensitive) as admin at the frontend layer. The backend
// `_is_admin_email()` check is the source of truth for privileged
// endpoints — this frontend fallback ONLY controls UI visibility.
function deriveIsAdmin(payload, user) {
  if (payload && typeof payload.is_admin === "boolean") return payload.is_admin;
  const email = ((user && user.email) || "").toLowerCase();
  if (user && user.is_admin === true) return true;
  if (email.startsWith("donnell")) return true;
  return false;
}

function cacheUser(u) {
  try {
    if (u) localStorage.setItem("crow_user_cache", JSON.stringify(u));
    else localStorage.removeItem("crow_user_cache");
  } catch { /* noop */ }
}

function readCachedUser() {
  try { return JSON.parse(localStorage.getItem("crow_user_cache") || "null"); } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("crow_token");
    if (!token) {
      setUser(null);
      setIsAdmin(false);
      setReady(true);
      return;
    }
    // Bounded /auth/me so a slow/cold backend can never freeze the app on "loading session…".
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await API.get("/auth/me", { timeout: 8000 });
        setUser(data.user);
        setIsAdmin(deriveIsAdmin(data, data.user));
        cacheUser(data.user);
        setReady(true);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("crow_token");
          cacheUser(null);
          setUser(null);
          setIsAdmin(false);
          setReady(true);
          return;
        }
      }
    }
    // Network error / timeout twice — keep the token and fall back to the last known user.
    const cached = readCachedUser();
    setUser(cached);
    setIsAdmin(cached ? deriveIsAdmin(null, cached) : false);
    setReady(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    localStorage.setItem("crow_token", data.token);
    cacheUser(data.user);
    setUser(data.user);
    setIsAdmin(deriveIsAdmin(data, data.user));
    return data;
  };

  const register = async (payload) => {
    // v19.31 — Attach UTM params captured at page load (either from
    // the URL or a persisted first-touch record) so signups get
    // attributed to their marketing source in the admin dashboard.
    let utm_source = "direct";
    let utm_campaign = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const stored = JSON.parse(localStorage.getItem("crow_utm") || "null");
      utm_source = params.get("utm_source") || stored?.utm_source || "direct";
      utm_campaign = params.get("utm_campaign") || stored?.utm_campaign || null;
      // Persist first-touch UTM so a signup on a later page still
      // attributes correctly.
      if (!stored && (params.get("utm_source") || params.get("utm_campaign"))) {
        localStorage.setItem("crow_utm", JSON.stringify({ utm_source, utm_campaign }));
      }
    } catch { /* noop */ }
    const { data } = await API.post("/auth/register", { ...payload, utm_source, utm_campaign });
    localStorage.setItem("crow_token", data.token);
    cacheUser(data.user);
    setUser(data.user);
    setIsAdmin(deriveIsAdmin(data, data.user));
    return data;
  };

  const logout = () => {
    localStorage.removeItem("crow_token");
    cacheUser(null);
    setUser(null);
    setIsAdmin(false);
  };

  const updateProfile = async (payload) => {
    const { data } = await API.post("/auth/profile", payload);
    cacheUser(data.user);
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAdmin,
      ready,
      login,
      register,
      logout,
      refresh,
      updateProfile,
      // v19.0 — Derived paywall state. `hasProducerAccess` is the single
      // source of truth for whether the current user can create/open
      // additional projects beyond their free trial beat.
      hasProducerAccess: !!(user?.is_admin || user?.has_producer_access),
      firstProjectId: user?.first_project_id || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
